import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Moon, Sparkles, ChevronDown, ChevronUp, CheckCircle2, Flame, Droplets, Dumbbell, Radar, Trash2, RefreshCw, X, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { TotalNutrition, UserGoals, Language, Vibe, SubscriptionTier } from '../types';
import { cn, getEveningWrapUpSessionDate } from '../lib/utils';
import { getDailyWrapUpSummary, enforceSingleUserNameInWrapUp } from '../services/nutritionService';
import { triggerHaptic } from '../lib/haptic';
import { sounds } from '../lib/sound';
import { db, auth } from '../services/firebase';
import { collection, doc, setDoc } from 'firebase/firestore';

interface DailyWrapUpCardProps {
  totals: TotalNutrition;
  goals: UserGoals;
  mealsCount: number;
  waterTotalMl: number;
  lang: Language;
  vibe: Vibe;
  skin?: string;
  subscriptionTier: SubscriptionTier;
  isEveningOverride?: boolean;
  onDismiss?: () => void;
  sessionDate?: string;
  isTransferred?: boolean;
  userName?: string;
}

export const DailyWrapUpCard: React.FC<DailyWrapUpCardProps> = ({
  totals,
  goals,
  mealsCount,
  waterTotalMl,
  lang,
  vibe,
  skin = 'default',
  subscriptionTier,
  isEveningOverride = false,
  onDismiss,
  sessionDate,
  isTransferred = false,
  userName
}) => {
  const effectiveSessionDate = sessionDate || getEveningWrapUpSessionDate();
  const totalsHash = `${Math.round(totals?.calories_kcal || 0)}_${Math.round(totals?.protein_g || 0)}_${Math.round(totals?.carbs_g || 0)}_${Math.round(totals?.fat_g || 0)}_${waterTotalMl}_${mealsCount}`;
  const effectiveUserName = userName || (typeof localStorage !== 'undefined' ? localStorage.getItem('userName') || '' : '');
  const cacheKey = `evening_wrapup_${effectiveSessionDate}_${totalsHash}_${lang}_${vibe}_${skin}_${effectiveUserName}`;

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [wrapUpData, setWrapUpData] = useState<{ paragraphs: string[]; isAiGenerated: boolean } | null>(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && Array.isArray(parsed.paragraphs) && parsed.paragraphs.length > 0) {
            return {
              ...parsed,
              paragraphs: enforceSingleUserNameInWrapUp(parsed.paragraphs, effectiveUserName, lang)
            };
          }
        }
      }
    } catch (e) {
      console.warn('Failed to read initial wrapup cache', e);
    }
    return null;
  });
  const [showContextMenu, setShowContextMenu] = useState(false);

  useEffect(() => {
    if (!showContextMenu) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowContextMenu(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showContextMenu]);

  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressActive = useRef(false);

  const isUa = lang.startsWith('ua');
  const isStalker = skin === 'stalker';

  // Check if it's 11:00 PM local time (hours >= 23 or hours < 4 for late night reading)
  const currentHour = new Date().getHours();
  const is11pmEvening = currentHour >= 23 || currentHour < 4;
  const shouldShow = is11pmEvening || isEveningOverride || isTransferred;

  // Free+ tiers check (free, pro, premium, premium_pro - all available tiers)
  const isAllowedTier = ['free', 'pro', 'premium', 'premium_pro', 'free+'].includes(subscriptionTier);

  // Optimized fetch with high-efficiency composite key caching
  const fetchWrapUp = useCallback(async (forceRefresh = false) => {
    const key = `evening_wrapup_${effectiveSessionDate}_${totalsHash}_${lang}_${vibe}_${skin}_${effectiveUserName}`;

    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(key);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && Array.isArray(parsed.paragraphs) && parsed.paragraphs.length > 0) {
            setWrapUpData({
              ...parsed,
              paragraphs: enforceSingleUserNameInWrapUp(parsed.paragraphs, effectiveUserName, lang)
            });
            return;
          }
        }
      } catch (e) {
        console.warn('Failed to read wrapup cache', e);
      }
    }

    setLoading(true);
    try {
      const summary = await getDailyWrapUpSummary(
        totals,
        goals,
        mealsCount,
        waterTotalMl,
        lang,
        vibe,
        skin,
        effectiveUserName
      );
      setWrapUpData(summary);
      try {
        localStorage.setItem(key, JSON.stringify(summary));
      } catch (e) {
        console.warn('Failed to cache wrapup', e);
      }
    } catch (err) {
      console.error('Wrap-up generation error:', err);
    } finally {
      setLoading(false);
    }
  }, [totalsHash, JSON.stringify(goals), mealsCount, waterTotalMl, lang, vibe, skin, effectiveSessionDate, effectiveUserName]);

  // Dispatch push notification when Evening Wrap-Up event is ready
  const triggerWrapUpNotification = useCallback(async () => {
    const notifSentKey = `evening_wrapup_notified_${effectiveSessionDate}`;

    if (localStorage.getItem(notifSentKey)) return;
    localStorage.setItem(notifSentKey, 'true');

    let notifTitle = '';
    let notifBody = '';

    if (isStalker) {
      if (isUa) {
        notifTitle = "☣️ КПК: Вечірній звіт завершено";
        notifBody = "Аналіз показників життєдіяльності та протокол нічної варти готові у вашому КПК.";
      } else {
        notifTitle = "☣️ PDA: Evening log completed";
        notifBody = "Biometric stability summary and night watch guidelines are ready in your PDA.";
      }
    } else if (vibe === 'casual') {
      if (isUa) {
        notifTitle = "Ей! Твій вечірній звіт готовий! 🌙";
        notifBody = "Час глянути, як ти закрив сьогоднішні цілі, бро! 🔥";
      } else {
        notifTitle = "Yo! Evening Wrap-Up is Ready, Homie! 🌙";
        notifBody = "Let's see how you crushed your goals, gains, and hydration today! 👊";
      }
    } else {
      if (isUa) {
        notifTitle = "🌙 Вечірній підсумок готовий!";
        notifBody = "Ваш підсумок харчування за день, баланс макронутрієнтів та поради на ніч вже доступні.";
      } else {
        notifTitle = "🌙 Evening Wrap-Up is Ready!";
        notifBody = "Your daily nutrition summary, macro breakdown, and night insights are now ready.";
      }
    }

    // 1. Native OS Push / Browser Notification
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          const reg = await navigator.serviceWorker.ready;
          reg.showNotification(notifTitle, {
            body: notifBody,
            icon: '/icon-512.png',
            badge: '/icon.svg',
            vibrate: [100, 50, 100],
            tag: 'evening-wrapup-ready'
          } as any);
        } else {
          new Notification(notifTitle, { body: notifBody, icon: '/icon-512.png' });
        }
      } catch (e) {
        console.warn("Could not dispatch native push notification:", e);
      }
    }

    // 2. In-App Notification entry in Firestore (if user is authenticated)
    if (auth.currentUser?.uid) {
      try {
        const notifsRef = collection(db, 'users', auth.currentUser.uid, 'notifications');
        const newNotifDoc = doc(notifsRef);
        const docId = newNotifDoc.id;

        // Prevent GamificationNotifications from showing this same notification natively again on this device
        localStorage.setItem(`native_notif_${docId}`, 'true');

        await setDoc(newNotifDoc, {
          timestamp: Date.now(),
          type: 'evening_wrapup',
          title: notifTitle,
          message: notifBody,
          read: false
        });
      } catch (e) {
        console.warn("Could not store in-app notification:", e);
      }
    }
  }, [isStalker, isUa, effectiveSessionDate, vibe]);

  useEffect(() => {
    if (shouldShow && isAllowedTier) {
      fetchWrapUp(false);
      triggerWrapUpNotification();
    }
  }, [shouldShow, isAllowedTier, effectiveSessionDate, totalsHash]);

  // Mark opened when card is expanded or interacted with
  const markAsOpened = useCallback(() => {
    try {
      localStorage.setItem(`evening_wrapup_opened_${effectiveSessionDate}`, 'true');
    } catch (e) {
      console.warn('Failed to mark evening wrapup as opened', e);
    }
  }, [effectiveSessionDate]);

  // Handle Long Press Gestures
  const startLongPress = () => {
    isLongPressActive.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressActive.current = true;
      triggerHaptic('SUCCESS');
      sounds.playPop();
      markAsOpened();
      setShowContextMenu(true);
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const isDismissedForSession = !isEveningOverride && (localStorage.getItem(`evening_wrapup_dismissed_${effectiveSessionDate}`) === 'true');

  if (!shouldShow || !isAllowedTier || isDismissedForSession) {
    return null;
  }

  const totalCalories = totals?.calories_kcal ?? 0;
  const totalProtein = totals?.protein_g ?? 0;
  const totalCarbs = totals?.carbs_g ?? 0;
  const totalFat = totals?.fat_g ?? 0;

  const goalCalories = goals?.calories || 2000;
  const goalProtein = goals?.protein || 100;
  const goalCarbs = goals?.carbs || 250;

  const calPercent = Math.min(100, Math.round((totalCalories / goalCalories) * 100));
  const protPercent = Math.min(100, Math.round((totalProtein / goalProtein) * 100));
  const carbsPercent = Math.min(100, Math.round((totalCarbs / goalCarbs) * 100));

  const handleDismiss = () => {
    triggerHaptic('SUCCESS');
    sounds.playPop();
    try {
      localStorage.setItem(`evening_wrapup_dismissed_${effectiveSessionDate}`, 'true');
    } catch (e) {
      console.warn('Failed to save evening wrapup dismissal', e);
    }
    onDismiss?.();
  };

  return (
    <div className="relative">
      <div
        onMouseDown={startLongPress}
        onMouseUp={cancelLongPress}
        onMouseLeave={cancelLongPress}
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchMove={cancelLongPress}
        onContextMenu={(e) => {
          e.preventDefault();
          triggerHaptic('SUCCESS');
          sounds.playPop();
          markAsOpened();
          setShowContextMenu(true);
        }}
        className={cn(
          "relative overflow-hidden rounded-3xl p-5 border card-shadow transition-shadow select-none",
          isStalker
            ? "bg-black border-emerald-500/50 text-emerald-400 font-mono"
            : "bg-[var(--card-bg)] border-[var(--card-border)] text-[var(--text)]"
        )}
      >
        {/* Decorative gradient background glows */}
        {!isStalker && (
          <>
            <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
          </>
        )}

        {/* Header bar */}
        <div 
          onClick={() => {
            if (!isLongPressActive.current) {
              triggerHaptic('WATER');
              const nextState = !isCollapsed;
              setIsCollapsed(nextState);
              if (!nextState) {
                markAsOpened();
              }
            }
          }}
          className="flex items-center justify-between gap-3 relative z-10 cursor-pointer select-none transition-all"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={cn(
              "w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 shadow-xs",
              isStalker 
                ? "bg-emerald-950/80 border border-emerald-500/40 text-emerald-400"
                : "bg-emerald-500/10 text-emerald-500 dark:bg-emerald-500/20"
            )}>
              {isStalker ? <Radar className="w-5 h-5 animate-pulse" /> : <Moon className="w-5 h-5 text-emerald-500" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className={cn(
                  "text-sm font-extrabold tracking-tight truncate",
                  isStalker ? "text-emerald-400 uppercase font-mono tracking-widest" : "text-zinc-900 dark:text-zinc-50"
                )}>
                  {isStalker 
                    ? (isTransferred
                        ? (isUa ? "ВЕЧІРНІЙ ПІДСУМОК (ВЧОРА)" : "EVENING WRAP-UP (YESTERDAY)")
                        : (isUa ? "ВЕЧІРНІЙ ПІДСУМОК" : "EVENING WRAP-UP"))
                    : (isTransferred
                        ? (isUa ? (vibe === 'casual' ? "Вечірній чек-ін (вчора) 🔥" : "Вечірній підсумок (вчора)") : (vibe === 'casual' ? "Evening Check-In (Yesterday) 🔥" : "Evening Wrap-Up (Yesterday)"))
                        : (isUa ? (vibe === 'casual' ? "Вечірній чек-ін 🔥" : "Вечірній підсумок") : (vibe === 'casual' ? "Evening Check-In 🔥" : "Evening Wrap-Up")))}
                </h3>
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap",
                  isStalker
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                    : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                )}>
                  {isStalker 
                    ? (isTransferred ? "Z-LOG (TRANSFERRED)" : "23:00 Z-LOG") 
                    : (isTransferred ? (isUa ? "Вчора" : "Yesterday") : (vibe === 'casual' ? (isUa ? "23:00 Чек-ін" : "11:00 PM Recap") : "11:00 PM"))}
                </span>
              </div>
              <p className={cn(
                "text-[11px] font-medium truncate mt-0.5",
                isStalker ? "text-emerald-500/70" : "text-zinc-500 dark:text-zinc-400"
              )}>
                {isStalker
                  ? (isTransferred
                      ? (isUa ? "Перенесений раціон за попередню добу" : "Transferred ration from previous watch")
                      : (isUa ? "Аналіз добового раціону та рекомендації перед вартою" : "Daily ration breakdown & night watch guidelines"))
                  : (isTransferred
                      ? (isUa ? (vibe === 'casual' ? "Вчорашній рекап (ще не відкривався)" : "Перенесено з вчорашнього дня (не відкривався)") : (vibe === 'casual' ? "Yesterday's recap (was not opened)" : "Transferred from yesterday (was not opened)"))
                      : (isUa ? (vibe === 'casual' ? "Твій рекап макросів, калорій та поради на ніч" : "Підсумок поживних речовин та порада на ніч") : (vibe === 'casual' ? "Your daily macro recap & night advice" : "Nutrient summary, advice & good night wish")))}
              </p>
            </div>
          </div>

          {/* Action Controls - Dismiss Button & Collapse Chevron */}
          <div className="flex items-center gap-1 shrink-0 relative z-10 ml-auto">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              title={isUa ? "Сховати підсумок" : "Dismiss wrap-up"}
              className={cn(
                "p-1.5 rounded-xl transition-all hover:bg-zinc-100 dark:hover:bg-zinc-800/80 cursor-pointer",
                isStalker
                  ? "text-emerald-500/60 hover:text-emerald-400"
                  : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              )}
            >
              <X className="w-4 h-4" />
            </button>
            <div
              title={isCollapsed ? (isUa ? "Розгорнути" : "Expand") : (isUa ? "Згорнути" : "Collapse")}
              className={cn(
                "p-1.5 rounded-xl transition-all",
                isStalker
                  ? "text-emerald-400"
                  : "text-zinc-500 dark:text-zinc-400"
              )}
            >
              {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </div>
          </div>
        </div>

        {/* Collapsible Content */}
        <AnimatePresence initial={false}>
          {!isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ 
                height: { duration: 0.28, ease: [0.25, 1, 0.5, 1] },
                opacity: { duration: 0.2, ease: "easeInOut" }
              }}
              className="overflow-hidden relative z-10"
            >
              <div className="pt-4 space-y-4">
              {/* Quick Macro Pill Highlights */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className={cn(
                  "p-2.5 rounded-2xl border flex flex-col justify-between",
                  isStalker 
                    ? "bg-emerald-950/30 border-emerald-500/30 text-emerald-400" 
                    : "bg-zinc-50 dark:bg-zinc-900/60 border-zinc-200/80 dark:border-zinc-800/80"
                )}>
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-emerald-500" /> {isUa ? "Калорії" : "Calories"}</span>
                    <span className="text-emerald-500 font-extrabold">{calPercent}%</span>
                  </div>
                  <div className="text-xs font-black mt-1 text-zinc-900 dark:text-zinc-100">
                    {Math.round(totalCalories)} <span className="text-[10px] font-normal text-zinc-400">/ {goalCalories}</span>
                  </div>
                  <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden mt-1.5">
                    <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${calPercent}%` }} />
                  </div>
                </div>

                <div className={cn(
                  "p-2.5 rounded-2xl border flex flex-col justify-between",
                  isStalker 
                    ? "bg-emerald-950/30 border-emerald-500/30 text-emerald-400" 
                    : "bg-zinc-50 dark:bg-zinc-900/60 border-zinc-200/80 dark:border-zinc-800/80"
                )}>
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3 text-emerald-500" /> {isUa ? "Білки" : "Protein"}</span>
                    <span className="text-emerald-500 font-extrabold">{protPercent}%</span>
                  </div>
                  <div className="text-xs font-black mt-1 text-zinc-900 dark:text-zinc-100">
                    {Math.round(totalProtein)}g <span className="text-[10px] font-normal text-zinc-400">/ {goalProtein}g</span>
                  </div>
                  <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden mt-1.5">
                    <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${protPercent}%` }} />
                  </div>
                </div>

                <div className={cn(
                  "p-2.5 rounded-2xl border flex flex-col justify-between",
                  isStalker 
                    ? "bg-emerald-950/30 border-emerald-500/30 text-emerald-400" 
                    : "bg-zinc-50 dark:bg-zinc-900/60 border-zinc-200/80 dark:border-zinc-800/80"
                )}>
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-500" /> {isUa ? "Вуглеводи" : "Carbs"}</span>
                    <span className="text-amber-500 font-extrabold">{carbsPercent}%</span>
                  </div>
                  <div className="text-xs font-black mt-1 text-zinc-900 dark:text-zinc-100">
                    {Math.round(totalCarbs)}g <span className="text-[10px] font-normal text-zinc-400">/ {goalCarbs}g</span>
                  </div>
                  <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden mt-1.5">
                    <div className="bg-amber-500 h-full rounded-full transition-all duration-500" style={{ width: `${carbsPercent}%` }} />
                  </div>
                </div>

                <div className={cn(
                  "p-2.5 rounded-2xl border flex flex-col justify-between",
                  isStalker 
                    ? "bg-emerald-950/30 border-emerald-500/30 text-emerald-400" 
                    : "bg-zinc-50 dark:bg-zinc-900/60 border-zinc-200/80 dark:border-zinc-800/80"
                )}>
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    <span className="flex items-center gap-1"><Droplets className="w-3 h-3 text-blue-500" /> {isUa ? "Вода" : "Water"}</span>
                    <span className="text-blue-500 font-extrabold">{(waterTotalMl / 1000).toFixed(1)}L</span>
                  </div>
                  <div className="text-xs font-black mt-1 text-zinc-900 dark:text-zinc-100">
                    {waterTotalMl} ml <span className="text-[10px] font-normal text-zinc-400">/ {goals?.water_ml || 2000} ml</span>
                  </div>
                  <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden mt-1.5">
                    <div className="bg-blue-500 h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, Math.round((waterTotalMl / (goals?.water_ml || 2000)) * 100))}%` }} />
                  </div>
                </div>
              </div>

              {/* Loading state skeleton */}
              {loading ? (
                <div className="space-y-3 py-2 animate-pulse">
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-800 rounded-lg w-11/12" />
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-800 rounded-lg w-full" />
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-800 rounded-lg w-4/5" />
                </div>
              ) : wrapUpData && wrapUpData.paragraphs.length > 0 ? (
                <div className="space-y-3 pt-1">
                  {enforceSingleUserNameInWrapUp(wrapUpData.paragraphs, effectiveUserName, lang).map((p, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "p-3.5 rounded-2xl text-xs leading-relaxed font-medium transition-all",
                        isStalker
                          ? "bg-emerald-950/20 border border-emerald-500/20 text-emerald-300"
                          : idx === 0
                          ? "bg-emerald-500/5 border border-emerald-500/10 text-zinc-800 dark:text-zinc-200"
                          : idx === 1
                          ? "bg-zinc-50 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80 text-zinc-700 dark:text-zinc-300"
                          : "bg-indigo-500/5 border border-indigo-500/10 text-zinc-800 dark:text-zinc-200 font-semibold"
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-emerald-500 shrink-0 font-extrabold mt-0.5">
                          {idx === 0 ? "📊" : idx === 1 ? "💡" : "🌙"}
                        </span>
                        <p className="flex-1">{p}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* Footer indicator */}
              <div className="flex items-center justify-between text-[10px] font-medium text-zinc-400 dark:text-zinc-500 pt-1">
                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  {wrapUpData?.isAiGenerated
                    ? (isUa ? "Персоналізовано Gemini AI" : "Personalized with Gemini AI")
                    : (isUa ? "Автоматичний вечірній підсумок" : "Automated Evening Summary")}
                </span>
                <span className="text-[9px] opacity-70">
                  {isUa ? "Утримуйте або натисніть ✕ для керування" : "Long press or tap ✕ to manage"}
                </span>
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tap & Hold Contextual Menu Modal */}
      <AnimatePresence>
        {showContextMenu && (
          <motion.div
            key="wrapup-menu-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={() => setShowContextMenu(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs"
          >
            <motion.div
              key="wrapup-menu-card"
              initial={{ opacity: 0, scale: 0.9, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 15 }}
              transition={{ type: "spring", stiffness: 350, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "w-full max-w-xs rounded-3xl p-5 border shadow-2xl relative overflow-hidden",
                isStalker
                  ? "bg-zinc-950 border-emerald-500 text-emerald-400 font-mono"
                  : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100"
              )}
            >
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-zinc-200/80 dark:border-zinc-800">
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-emerald-500" />
                  <h4 className="text-sm font-bold">
                    {isUa ? "Evening Wrap-Up" : "Evening Wrap-Up"}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => setShowContextMenu(false)}
                  className="p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowContextMenu(false);
                    triggerHaptic('SUCCESS');
                    sounds.playPop();
                    fetchWrapUp(true);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-xs font-semibold transition-all cursor-pointer",
                    isStalker
                      ? "hover:bg-emerald-950/60 text-emerald-400"
                      : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200"
                  )}
                >
                  <RefreshCw className={cn("w-4 h-4 text-emerald-500", loading && "animate-spin")} />
                  <span>{isUa ? "Оновити аналіз AI" : "Regenerate Summary"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowContextMenu(false);
                    handleDismiss();
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-xs font-semibold transition-all cursor-pointer",
                    isStalker
                      ? "hover:bg-red-950/60 text-red-400"
                      : "hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 dark:text-red-400"
                  )}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                  <span>{isUa ? "Сховати вечірній підсумок" : "Remove Evening Wrap-Up"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowContextMenu(false)}
                  className="w-full py-2 text-center text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 font-medium cursor-pointer"
                >
                  {isUa ? "Скасувати" : "Cancel"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
